import { COMMAND_KINDS, type Command, type TimedCommand } from './commands.js';
import type { RunConfig } from './world.js';
import { World } from './world.js';

/**
 * Run serialization and resume (TRD-10 §3).
 *
 * ── The command log IS the save file ──────────────────────────────────────────────────
 * A run is stored as `(seed, config, commands, tick)` — never as a world-state dump.
 * Resuming replays the log at full speed to reach the saved tick.
 *
 * That is the payoff of the determinism work: a 25-wave run is a few hundred commands and
 * a handful of KB, where a state dump would be hundreds and would need a migration every
 * time an entity field changed. It also means save/resume and server-side validation
 * exercise exactly the same code path, so a bug in one shows up in the other rather than
 * hiding until submission.
 */

export interface SerializedRun {
  /** Decimal string: `JSON.stringify` throws on BigInt, and hex round-trips lose
   *  precision above 2^53 (R-10-07). */
  seed: string;
  config: RunConfig;
  commands: TimedCommand[];
  currentTick: number;
  isDaily: boolean;
  dailyDate?: string;
  /** Guards against resuming a run whose rules changed underneath it (R-10-10). */
  hashAtSave: number;
  simVersion: number;
}

export interface ResumeResult {
  world: World;
  /** False when the replay diverged — the run must be discarded, not continued. */
  ok: boolean;
  reason?: 'hash-mismatch' | 'sim-version';
}

export function serializeRun(
  world: World,
  commands: readonly TimedCommand[],
  options: { isDaily?: boolean; dailyDate?: string } = {},
): SerializedRun {
  return {
    seed: world.seed.toString(10),
    config: world.config,
    commands: commands.map((c) => ({ tick: c.tick, cmd: c.cmd })),
    currentTick: world.tick,
    isDaily: options.isDaily ?? false,
    ...(options.dailyDate !== undefined ? { dailyDate: options.dailyDate } : {}),
    hashAtSave: world.runHash,
    simVersion: world.config.simVersion,
  };
}

/**
 * Rebuild a world by replaying its own log.
 *
 * The hash check at the end is the point: if content or the engine changed since the save,
 * the replay lands somewhere different and the run is discarded with a clear message.
 * Letting it continue would produce a run that plays fine and is then rejected at
 * submission with nothing to explain it (R-10-10).
 */
export function resumeRun(saved: SerializedRun, currentSimVersion: number): ResumeResult {
  const world = new World(BigInt(saved.seed), saved.config);

  if (saved.simVersion !== currentSimVersion) {
    return { world, ok: false, reason: 'sim-version' };
  }

  const byTick = new Map<number, Command[]>();
  for (const { tick, cmd } of saved.commands) {
    const bucket = byTick.get(tick);
    if (bucket) bucket.push(cmd);
    else byTick.set(tick, [cmd]);
  }

  const empty: readonly Command[] = [];
  while (world.tick < saved.currentTick && world.phase !== 'ended') {
    world.step(byTick.get(world.tick) ?? empty);
  }

  if (world.runHash !== saved.hashAtSave) {
    return { world, ok: false, reason: 'hash-mismatch' };
  }
  return { world, ok: true };
}

// ── Compact encoding (R-10-18) ────────────────────────────────────────────────────────

/**
 * `[tick, kindIndex, ...args]`, flattened.
 *
 * A full run is 150–400 commands, so this is small either way — the reason to bother is
 * that it keeps the submission payload comfortably under the server's 64 KB body limit
 * even for a pathological log, and makes the rate limit easy to reason about.
 *
 * The kind index comes from `COMMAND_KINDS`, whose ORDER is therefore part of the wire
 * format: appending is safe, reordering silently reinterprets every stored log.
 */
export function encodeCommands(commands: readonly TimedCommand[]): number[] {
  const out: number[] = [];
  for (const { tick, cmd } of commands) {
    const kind = COMMAND_KINDS.indexOf(cmd.t);
    if (kind < 0) continue;
    out.push(tick, kind);
    switch (cmd.t) {
      case 'buy':
        out.push(cmd.slot);
        break;
      case 'place':
        out.push(cmd.benchIndex, cmd.tileX, cmd.tileY);
        break;
      case 'move':
        out.push(cmd.tower, cmd.tileX, cmd.tileY);
        break;
      case 'sell':
        out.push(cmd.tower);
        break;
      case 'sellBench':
        out.push(cmd.benchIndex);
        break;
      case 'setTargeting':
        out.push(cmd.tower, TARGETING_MODES.indexOf(cmd.mode));
        break;
      case 'reroll':
      case 'startWave':
        break;
    }
  }
  return out;
}

const TARGETING_MODES = ['first', 'last', 'closest', 'strongest', 'weakest'] as const;

export function decodeCommands(encoded: readonly number[]): TimedCommand[] {
  const out: TimedCommand[] = [];
  let i = 0;

  while (i < encoded.length) {
    const tick = encoded[i++] as number;
    const kind = COMMAND_KINDS[encoded[i++] as number];
    if (kind === undefined) break;

    switch (kind) {
      case 'buy':
        out.push({ tick, cmd: { t: 'buy', slot: encoded[i++] as number } });
        break;
      case 'reroll':
        out.push({ tick, cmd: { t: 'reroll' } });
        break;
      case 'place':
        out.push({
          tick,
          cmd: {
            t: 'place',
            benchIndex: encoded[i++] as number,
            tileX: encoded[i++] as number,
            tileY: encoded[i++] as number,
          },
        });
        break;
      case 'move':
        out.push({
          tick,
          cmd: {
            t: 'move',
            tower: encoded[i++] as number,
            tileX: encoded[i++] as number,
            tileY: encoded[i++] as number,
          },
        });
        break;
      case 'sell':
        out.push({ tick, cmd: { t: 'sell', tower: encoded[i++] as number } });
        break;
      case 'sellBench':
        out.push({ tick, cmd: { t: 'sellBench', benchIndex: encoded[i++] as number } });
        break;
      case 'setTargeting': {
        const tower = encoded[i++] as number;
        const mode = TARGETING_MODES[encoded[i++] as number] ?? 'first';
        out.push({ tick, cmd: { t: 'setTargeting', tower, mode } });
        break;
      }
      case 'startWave':
        out.push({ tick, cmd: { t: 'startWave' } });
        break;
    }
  }

  return out;
}

/**
 * The score submission contract (TRD-10 §5).
 *
 * Declared here, in the package the client and the server both already depend on, because
 * two independent declarations of a wire format drift — and the drift surfaces as
 * submissions rejected for reasons neither side logs. The server's Zod schema validates
 * against this type; a mismatch is a compile error rather than a support ticket.
 */
/**
 * Every code `/api/submit` can answer with, and which of them a retry cannot fix.
 *
 * Lives HERE, in the package both sides already depend on, because it is a wire contract
 * and it had drifted badly: the client's own list named two codes the server never sends
 * (`DUPLICATE`, `INVALID_DATE`) and omitted four it does, so a submission rejected for a
 * bad date or an over-long log was retried three times and then queued forever.
 */
export const SUBMIT_ERROR_CODES = [
  'BAD_REQUEST',
  'STALE_CLIENT',
  'BAD_DATE',
  'LOG_TOO_LONG',
  'LOG_NOT_MONOTONIC',
  'BAD_NAME',
  'RATE_LIMITED',
  'SCORE_MISMATCH',
  'HASH_MISMATCH',
  'REPLAY_TIMEOUT',
  'OVERLOADED',
  'REPLAY_FAILED',
] as const;

export type SubmitErrorCode = (typeof SUBMIT_ERROR_CODES)[number];

/**
 * Codes where re-sending the SAME payload can never succeed.
 *
 * The complement — RATE_LIMITED, REPLAY_TIMEOUT, OVERLOADED, REPLAY_FAILED — is about
 * the SERVICE rather than the run. `REPLAY_FAILED` is deliberately in that group: a
 * replay that throws is our bug, and the player's run should outlive it and count once
 * the crash is fixed rather than being thrown away as if they had sent nonsense.
 */
export const TERMINAL_SUBMIT_ERRORS: ReadonlySet<string> = new Set<SubmitErrorCode>([
  'BAD_REQUEST',
  'STALE_CLIENT',
  'BAD_DATE',
  'LOG_TOO_LONG',
  'LOG_NOT_MONOTONIC',
  'BAD_NAME',
  'SCORE_MISMATCH',
  'HASH_MISMATCH',
]);

export interface SubmissionPayload {
  date: string;
  playerId: string;
  displayName: string;
  claimedScore: number;
  /** `World.runHash` — the ROLLING digest, never `hash()` (TRD-00 R-00-08). */
  runHash: number;
  simVersion: number;
  clientVersion: string;
  finalTick: number;
  /** Decimal digits of the run's shop seed — the shop is per-run, not per-day. */
  shopSeed?: string;
  commands: TimedCommand[];
}
